import React from 'react';
import { Sidebar } from '../components/Sidebar';
import { Alert, IncidentMetrics } from '../types';
import { formatTimestamp } from '../utils/helpers';

interface DashboardPageProps {
  currentPage: string;
  activeAlertsCount: number;
  alerts: Alert[];
  alertsPagination: {
    current_page: number;
    total_pages: number;
    total_items: number;
    per_page: number;
    has_next: boolean;
    has_prev: boolean;
  } | null;
  incidentMetrics: IncidentMetrics | null;
  incidentMetricsLoading: boolean;
  onNavigate: (page: string) => void;
  onLogout: () => void;
}

export const DashboardPage: React.FC<DashboardPageProps> = ({
  currentPage,
  activeAlertsCount,
  alerts,
  alertsPagination,
  incidentMetrics,
  incidentMetricsLoading,
  onNavigate,
  onLogout,
}) => {
  // Calculate alert metrics
  const totalAlerts = alertsPagination?.total_items ?? alerts.length;
  const activeAlerts = alerts.filter(alert => alert.status === 'FIRING');
  const currentPageActiveAlerts = activeAlerts.length;
  const currentPageResolvedAlerts = alerts.filter(alert => alert.status === 'RESOLVED').length;
  const currentPageTotal = currentPageActiveAlerts + currentPageResolvedAlerts;
  const totalActiveAlerts = currentPageTotal > 0
    ? Math.round((currentPageActiveAlerts / currentPageTotal) * totalAlerts)
    : 0;
  const totalResolvedAlerts = totalAlerts - totalActiveAlerts;

  // Helper function to format time in minutes to hours
  const formatTime = (minutes: number | undefined): string => {
    if (!minutes) return 'N/A';
    const hours = minutes / 60;
    return `${hours.toFixed(1)}h`;
  };

  return (
    <div className="h-screen bg-[#1A1A1A] text-gray-100 flex overflow-hidden">
      <Sidebar
        currentPage={currentPage}
        activeAlertsCount={activeAlertsCount}
        onNavigate={onNavigate}
        onLogout={onLogout}
      />
      <div className="flex-1 ml-64 p-6 overflow-y-auto">
        <h1 className="text-3xl font-bold mb-6 text-white">Dashboard</h1>

        {/* System Overview */}
        <div className="bg-[#222222] rounded-lg p-6 mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-xl font-semibold text-white mb-1">System Health</h3>
              <p className="text-sm text-[#8b949e]">Bazaar</p>
            </div>
            <div className="flex items-center space-x-8">
              <div className="text-center">
                <div className="text-3xl font-bold text-white">{totalAlerts}</div>
                <div className="text-sm text-[#8b949e]">Total Alerts</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-red-400">{totalActiveAlerts}</div>
                <div className="text-sm text-[#8b949e]">Active</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-green-400">{totalResolvedAlerts}</div>
                <div className="text-sm text-[#8b949e]">Resolved</div>
              </div>
              {(incidentMetrics?.mttd_minutes || incidentMetrics?.mttr_minutes) && (
                <>
                  <div className="text-center">
                    <div className="text-3xl font-bold text-blue-400">
                      {formatTime(incidentMetrics?.mttd_minutes)}
                    </div>
                    <div className="text-sm text-[#8b949e]">MTTD</div>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-bold text-orange-400">
                      {formatTime(incidentMetrics?.mttr_minutes)}
                    </div>
                    <div className="text-sm text-[#8b949e]">MTTR</div>
                  </div>
                </>
              )}
            </div>
          </div>
          {incidentMetricsLoading && (
            <div className="mt-4 text-center text-[#8b949e] text-sm">
              Loading metrics...
            </div>
          )}
        </div>

        {/* Recent Alerts */}
        <div className="bg-[#222222] rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-xl font-semibold text-white">Recent Alerts</h3>
            <button
              onClick={() => onNavigate('alerts')}
              className="text-[#8b949e] hover:text-white text-sm font-medium transition-colors"
            >
              View All →
            </button>
          </div>

          {alerts.length === 0 ? (
            <div className="text-center py-12 text-[#8b949e]">
              <p>No alerts found</p>
            </div>
          ) : (
            <div className="space-y-3">
              {alerts.slice(0, 10).map((alert) => (
                <div
                  key={alert.id}
                  onClick={() => onNavigate('alerts')}
                  className="border border-gray-600 rounded-lg p-4"
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center space-x-3">
                      <div className={`w-3 h-3 rounded-full ${
                        alert.status === 'RESOLVED' ? 'bg-green-500' : 'bg-red-500'
                      }`}></div>
                      <h4 className="font-medium text-white">{alert.title}</h4>
                    </div>
                    <div className="flex items-center space-x-2">
                      {alert.status === 'RESOLVED' && (
                        <span className="px-2 py-1 bg-green-600 text-white text-xs rounded">
                          RESOLVED
                        </span>
                      )}
                      {alert.auto_fix_available && (
                        <span className="px-2 py-1 bg-blue-600 text-white text-xs rounded">
                          AUTO-FIX
                        </span>
                      )}
                      <span className={`px-2 py-1 text-xs rounded ${
                        alert.priority === 1
                          ? 'bg-red-600 text-white'
                          : 'bg-yellow-600 text-white'
                      }`}>
                        P{alert.priority}
                      </span>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-[#8b949e]">Service: </span>
                      <span className="text-white">{alert.service_name}</span>
                    </div>
                    <div>
                      <span className="text-[#8b949e]">Summary: </span>
                      <span className="text-white">{alert.annotations.summary}</span>
                    </div>
                  </div>
                  {alert.ends_at && (
                    <div className="text-xs text-[#8b949e] mt-2">
                      Resolved {formatTimestamp(alert.ends_at)}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
